import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FeedbackThumbs from "./Feedback.Thumbs";
import RadioGroup from "../Radio/Group";
import { FeedbackProps, FeedbackData } from "./types";
import { styles } from "./styles";
import Button from "../Button";
import ButtonGroup from "../Button/Group";
import TextArea from "../TextArea";

/**
 * `Feedback` component combining a thumbs up/down for sentiment,
 * a chip-style RadioGroup for categorical feedback,
 * and a text area for detailed feedback.
 */
function Feedback({
  onChange,
  onSubmit,
  tags: _tags,
  placeholder,
  variant = "default",
  captureDelay = 5_000,
  completedMessage = "Thank you!"
}: FeedbackProps) {
  const [feedback, setFeedback] = useState<FeedbackData>({});
  const [formCompleted, setFormCompleted] = useState(false);
  // Ref to track feedback state in unmounted scenarios and to manage debounced submission
  const feedbackRef = useRef<FeedbackData>({});
  // Ref to track the last submitted feedback for comparison to prevent duplicate submissions
  const submittedFeedbackRef = useRef<FeedbackData>({});
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const textareaFocusedRef = useRef(false);

  const hasSentiment = feedback.sentiment !== undefined;
  const showAll = variant === "all";

  const showFeedbackText =
    showAll ||
    (variant === "default" ? hasSentiment : feedback.tag !== undefined);

  const options = useMemo(() => {
    if (!_tags) {
      return undefined;
    }
    if (Array.isArray(_tags) && (showAll || feedback.sentiment)) {
      return _tags;
    }
    if (!Array.isArray(_tags) && feedback.sentiment) {
      return _tags[feedback.sentiment];
    }
    return undefined;
  }, [_tags, feedback.sentiment]);

  /**
   * Sets a debounced submit action that will trigger after the specified captureDelay.
   * This allows for capturing feedback without requiring an explicit submit action,
   * while also preventing excessive submissions during active feedback entry.
   */
  function setDebouncedSubmit(newFeedback: FeedbackData = feedback) {
    clearSubmitTimeout();
    if (
      !newFeedback.sentiment ||
      !isNewFeedback(submittedFeedbackRef.current, newFeedback)
    ) {
      return;
    }
    submitTimeoutRef.current = setTimeout(handleSubmit, captureDelay);
  }

  function clearSubmitTimeout() {
    if (submitTimeoutRef.current) {
      clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }

  function handleSetFeedback(data: FeedbackData) {
    setFeedback(data);
    feedbackRef.current = data;
  }

  function handleChange(updates: Partial<FeedbackData>) {
    const newFeedback = { ...feedback, ...updates };
    if (newFeedback.sentiment && newFeedback.sentiment !== feedback.sentiment) {
      // If sentiment has changed, clear tag since it is no longer be relevant
      delete newFeedback.tag;
    }
    setFormCompleted(false);
    handleSetFeedback(newFeedback);
    onChange?.(newFeedback);
    if (!textareaFocusedRef.current) {
      setDebouncedSubmit(newFeedback);
    }
  }

  const handleSubmit = useCallback(() => {
    if (!isNewFeedback(submittedFeedbackRef.current, feedbackRef.current)) {
      return;
    }
    submittedFeedbackRef.current = feedbackRef.current;
    onSubmit(feedbackRef.current);
  }, [onSubmit]);

  function handleCancel() {
    clearSubmitTimeout();
    handleSetFeedback({});
    submittedFeedbackRef.current = {};
  }

  useEffect(() => {
    return () => {
      // On unmount, always clear any pending timeout
      clearSubmitTimeout();
      // handleSubmit() will no-op if there is nothing new.
      handleSubmit();
    };
  }, [handleSubmit]);

  return (
    <div className="kit-Feedback" css={styles.section}>
      <FeedbackThumbs
        value={feedback.sentiment}
        onChange={sentiment => handleChange({ sentiment })}
      />
      {(options || showFeedbackText || formCompleted) && (
        <div
          className="kit-feedback-details"
          css={[styles.section, styles.details]}
        >
          {!formCompleted && options && (
            <RadioGroup
              variant="chip"
              options={options}
              value={feedback.tag}
              lookupLabel={option => option}
              lookupValue={option => option}
              onChange={selected => handleChange({ tag: selected })}
            />
          )}
          {!formCompleted && showFeedbackText && (
            <div className="kit-feedback-comment" css={[styles.section]}>
              <TextArea
                value={feedback.text || ""}
                onChange={val => handleChange({ text: val })}
                placeholder={placeholder}
                fill
                onFocus={() => {
                  textareaFocusedRef.current = true;
                  clearSubmitTimeout();
                }}
                onBlur={() => {
                  textareaFocusedRef.current = false;
                  setDebouncedSubmit(feedbackRef.current);
                }}
              />
              <div css={styles.feedbackButtons}>
                <ButtonGroup>
                  <Button
                    onClick={handleCancel}
                    variant="ghost"
                    data-testid="kit-Feedback-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      handleSubmit();
                      setFormCompleted(true);
                    }}
                    variant="primary"
                    data-testid="kit-Feedback-submit"
                  >
                    Submit
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          )}
          {formCompleted && (
            <div css={styles.completedMessage}>{completedMessage}</div>
          )}
        </div>
      )}
    </div>
  );
}

function isNewFeedback(oldFeedback: FeedbackData, newFeedback: FeedbackData) {
  const isEmpty = !Object.values(newFeedback).some(
    val => val !== "" && val !== undefined
  );
  if (isEmpty) {
    return false;
  }
  return (
    oldFeedback.sentiment !== newFeedback.sentiment ||
    oldFeedback.tag !== newFeedback.tag ||
    oldFeedback.text !== newFeedback.text
  );
}

Feedback.Thumbs = FeedbackThumbs;
Feedback.Thumbs.displayName = "Feedback.Thumbs";

export default Feedback;
